<template>
  <div>
    <PTransparentLoader :loading="loading">
    <CRow>
      <CCol lg="12">
         <h1 class="main-header">
              <translate translate-context="admin-payout">Payouts Management</translate>
            </h1>
        <!-- Lista Payouts -->
        <CCard>
          <CCardHeader class="border-0 ml-3">
            <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipWallet02" size="custom-size" :width="18"/>
            <span class="mx-1" v-translate translate-context="accounting">All payouts</span>
                <span>({{ payouts.pagination.total|number }})</span>
          </div>
          </CCardHeader>
          <!-- Statistics summary + Generate for CM scope -->
          <CCardBody v-if="contractManagerId && statistics" class="px-0">
            <CRow class="align-items-center">
              <CCol sm="3">
                <div class="stat-card border rounded p-3 text-center">
                  <div class="text-muted small mb-1">
                    <translate translate-context="admin-payout">Available for payout</translate>
                  </div>
                  <div class="h4 mb-0" :class="hasPendingBalance ? 'text-primary' : 'text-muted'">
                    {{ statistics.pending_balance | currency(statistics.currency || 'EUR') }}
                  </div>
                </div>
              </CCol>
              <CCol sm="3">
                <div class="stat-card border rounded p-3 text-center">
                  <div class="text-muted small mb-1">
                    <translate translate-context="admin-payout">Total payouts</translate>
                  </div>
                  <div class="h4 mb-0 text-success">
                    {{ statistics.total_payouts | currency(statistics.currency || 'EUR') }}
                  </div>
                </div>
              </CCol>
              <CCol sm="3">
                <div class="stat-card border rounded p-3 text-center">
                  <div class="text-muted small mb-1">
                    <translate translate-context="admin-payout">Revenue / Fees</translate>
                  </div>
                  <div class="h4 mb-0">
                    {{ statistics.total_revenue | currency(statistics.currency || 'EUR') }}
                    <span class="text-muted h6">/ {{ statistics.total_fees | currency(statistics.currency || 'EUR') }}</span>
                  </div>
                </div>
              </CCol>
              <CCol sm="3" v-if="canGenerate">
                <div class="stat-card border rounded p-3 text-center">
                  <CButton
                    color="primary"
                    :disabled="!hasPendingBalance"
                    @click="handleGeneratePayout"
                    class="w-100"
                  >
                    <CIcon name="cipPlus" size="sm" class="mr-1"/>
                    <translate translate-context="admin-payout">Generate Payout</translate>
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </CCardBody>

          <CCardBody class="px-0">
            <AjaxTable
              ref="ajaxTable"
              :fields="fields"
              :data="payouts"
              :fetch="fetchList"
                 class="custom-table custom-table--clean custom-table--has-calendar mx-4"
            >
              <!-- Clickable ID -->
              <template #id="{item}">
                <td>
                  <CLink
                    v-if="canViewDetail"
                    :to="getDetailRoute(item.id)"
                    class="font-weight-semi-bold"
                  >
                    {{ item.id }}
                  </CLink>
                  <span v-else class="font-weight-semi-bold">#{{ item.id }}</span>
                </td>
              </template>

              <!-- Status filter dropdown -->
              <template #status-filter>
                <CSelect
                  :value.sync="statusFilterValue"
                  @update:value="onStatusFilterChange"
                  :options="statusOptions"
                  size="sm"
                />
              </template>

              <template #status="{item}">
                <td>
                  <CBadge :color="getStatusColor(item.status)" class="badge-outline">
                    <div class="d-flex align-items-center">
                      <CIcon :name="getStatusIcon(item.status)"  class="mr-1"/>
                      <span class="font-weight-normal">{{ getStatusLabel(item.status) }}</span>
                    </div>
                  </CBadge>
                </td>
              </template>
              
              <template #paid_net="{item}">
                <td>
                  <div>
                    <div class="font-weight-medium">{{ item.paid_net | currency(item.currency) }}</div>
                    <div class="small text-muted" v-if="item.payout_pica_services && item.payout_pica_services.length">
                      {{ item.payout_pica_services.length }} <translate translate-context="admin-payout">services</translate>
                    </div>
                  </div>
                </td>
              </template>

              <template #contract_manager_name="{item}">
                <td>
                  <CLink
                    v-if="item.contract_manager_id"
                    :to="{name: 'contract_manager.detail', params: {id: item.contract_manager_id}}"
                  >
                    {{ item.contract_manager_name }}
                  </CLink>
                  <span v-else>{{ item.contract_manager_name }}</span>
                </td>
              </template>
              
              <template #created_at="{item}">
                <td>
                  {{ item.created_at | formatDateTime('L LT') }}
                </td>
              </template>
              
              <template #paid_at="{item}">
                <td>
                  <span v-if="item.paid_at">{{ item.paid_at | formatDateTime('L LT') }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>
              
              <template #notes="{item}">
                <td>
                  <div v-if="item.notes" class="text-truncate" style="max-width: 200px;" v-c-tooltip="item.notes">
                    {{ item.notes }}
                  </div>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>
              
              <template #invoice="{item}">
                <td>
                  <template v-if="item.invoice">
                    <CLink :href="item.invoice" target="_blank" class="d-flex align-items-center link-invoice">
                      <CIcon name="cipDoc" size="sm" class="mr-1"/>
                      <translate translate-context="admin-payout">Invoice</translate>
                    </CLink>
                  </template>
                  <template v-else>
                    <span class="text-muted">-</span>
                  </template>
                </td>
              </template>
              
              <template #actions="{item}">
                <td>
                  <div class="d-flex align-items-center">
                    <CButton
                      v-if="canMarkPaid && item.status === payoutStatus.invoice_uploaded"
                      color="success"
                      size="sm"
                      variant="outline"
                      @click="handleMarkPayoutAsPaid(item)"
                      class="mr-2"
                    >
                      <CIcon name="cipCheck" size="sm" class="mr-1"/>
                      <translate translate-context="admin-payout">Mark paid</translate>
                    </CButton>

                    <CButton
                      v-if="canRejectInvoice && item.status === payoutStatus.invoice_uploaded"
                      color="danger"
                      size="sm"
                      variant="outline"
                      @click="openRejectModal(item)"
                      class="mr-2"
                    >
                      <CIcon name="cipXCircle" size="sm" class="mr-1"/>
                      <translate translate-context="admin-payout">Reject</translate>
                    </CButton>

                    <CButton
                      v-if="canDownload && item.pica_services && item.pica_services.length"
                      color="dark"
                      size="sm"
                      variant="outline"
                      @click="downloadCSV(item)"
                      class="mr-2"
                    >
                      <CIcon name="cipDownload" size="sm"/>
                    </CButton>

                     <CButton
                      v-if="canDeletePayout && item.status !== payoutStatus.paid && item.is_last_unpaid"
                      color="danger"
                      size="sm"
                      variant="outline"
                      @click="handleDeletePayout(item)"
                    >
                      <CIcon name="cipTrashFull" size="sm"/>
                    </CButton>
                    
                    <span v-if="!canMarkPaid && !canDeletePayout && !canDownload" class="text-muted">-</span>
                  </div>
                </td>
              </template>

            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    </PTransparentLoader>
    <RejectInvoiceModal ref="rejectInvoiceModal" @rejected="onInvoiceRejected"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import RejectInvoiceModal from './RejectInvoiceModal.vue'
import payoutStatus from './constants'

export default {
  name: 'AdminPayoutList',
  components: {
    AjaxTable,
    PTransparentLoader,
    RejectInvoiceModal
  },
  data() {
    return {
      loading: false,
      statusFilterValue: payoutStatus.invoice_uploaded,
      payoutStatus, // Make payoutStatus available in template
      fields: [
        {
          key: 'id',
          label: this.$pgettext('admin-payout', 'ID'),
          _style: 'width: 80px',
          sorter: true,
        },
        {
          key: 'status',
          label: this.$pgettext('admin-payout', 'Status'),
          _style: 'width: 150px',
          filter: true,
        },
        {
          key: 'paid_net',
          label: this.$pgettext('admin-payout', 'Net Amount'),
          _style: 'width: 130px',
          sorter: true,
        },
        {
          key: 'contract_manager_name',
          label: this.$pgettext('admin-payout', 'Contract Manager'),
          _style: 'width: 200px',
        },
        {
          key: 'created_at',
          label: this.$pgettext('admin-payout', 'Created'),
          _style: 'width: 150px',
          sorter: true,
        },
        {
          key: 'paid_at',
          label: this.$pgettext('admin-payout', 'Paid'),
          _style: 'width: 150px',
          sorter: true,
        },
        {
          key: 'notes',
          label: this.$pgettext('admin-payout', 'Notes'),
          _style: 'width: 180px',
        },
        {
          key: 'invoice',
          label: this.$pgettext('admin-payout', 'Invoice'),
          filter: false,
          _style: 'width: 100px',
        },
        {
          key: 'actions',
          label: this.$pgettext('admin-payout', 'Actions'),
          sorter: false,
          filter: false,
          _style: 'width: 150px',
        }
      ]
    }
  },
  computed: {
    ...mapGetters('adminPayout', ['payouts', 'statistics']),
    ...mapGetters('login', ['hasAnyPerms', 'config']),
    canMarkPaid() {
      return this.hasAnyPerms(['admin.payouts.mark_paid'])
    },
    canViewDetail() {
      return this.hasAnyPerms(['admin.payouts.detail'])
    },
    canDeletePayout() {
      return this.hasAnyPerms(['admin.payouts.delete'])
    },
    canDownload() {
      return this.hasAnyPerms(['admin.payouts.download'])
    },
    canRejectInvoice() {
      return this.hasAnyPerms(['admin.payouts.reject_invoice', 'admin.payouts.mark_paid'])
    },
    canGenerate() {
      return this.hasAnyPerms(['admin.payouts.generate'])
    },
    contractManagerId() {
      return this.$route.params.contractManagerId || null
    },
    hasPendingBalance() {
      return this.statistics && parseFloat(this.statistics.pending_balance) > 0
    },
    statusOptions() {
      // Use the payoutStatus constant's method to get properly formatted options
      return payoutStatus.getSelectOptions('admin-payout', true)
    }
  },
  methods: {
    ...mapActions('adminPayout', ['fetchAdminPayoutList', 'fetchStatistics', 'markPayoutAsPaid', 'downloadPayoutExcel', 'deleteLastPayout', 'generatePayout']),
    ...mapActions('login', ['fetchConfig']),
    
    async fetchList(params) {
      const filters = {...params.filters}

      // Apply contract_manager filter from route params (Django double underscore)
      if (this.contractManagerId) {
        filters.contract_manager__id = this.contractManagerId
      }

      // Handle status filter
      if (Object.keys(filters).length === 0 && !filters.status) {
        filters.status = this.statusFilterValue
      } else if (filters.status) {
        this.statusFilterValue = filters.status
      } else if (this.statusFilterValue && this.statusFilterValue !== payoutStatus.all) {
        filters.status = this.statusFilterValue
      }

      if (filters.status === payoutStatus.all) {
        delete filters.status
      }

      await this.fetchAdminPayoutList({
        ...params,
        filters
      })
    },
    
    getDetailRoute(id) {
      if (this.contractManagerId) {
        return {name: 'contractManager.payoutDetail', params: {contractManagerId: this.contractManagerId, id}}
      }
      return {name: 'adminPayoutDetail', params: {id}}
    },

    getStatusLabel(status) {
      return payoutStatus.label(status)
    },
    
    getStatusColor(status) {
      return payoutStatus.color(status)
    },
    
    getStatusIcon(status) {
      return payoutStatus.icon(status)
    },
    
    onStatusFilterChange() {
      // Update the filters and trigger refetch
      const filters = this.statusFilterValue === payoutStatus.all 
        ? {} 
        : { status: this.statusFilterValue }
      
      if (this.$refs.ajaxTable) {
        this.$refs.ajaxTable.filters = filters
        this.$refs.ajaxTable.pagination.page = 1
        this.$refs.ajaxTable.fetchList()
      }
    },
    
    handleMarkPayoutAsPaid(payout) {
      this.confirm({
        title: this.$pgettext('admin-payout', 'Are you sure you made the transfer?'),
        message: this.$pgettext('admin-payout', 'If you confirm, the action will be irreversible'),
        icon: 'cipInfo',
        cb_confirm: async () => {
          try {
            await this.markPayoutAsPaid({id: payout.id, data: {}})
            await this.$refs.ajaxTable?.fetchList()
            await this.notifySuccess(this.$pgettext('admin-payout', 'Payout marked as paid successfully'))
          } catch (error) {
            await this.notifyError(this.$pgettext('admin-payout', 'Error marking payout as paid'))
          }
        }
      })
    },
    
    handleDeletePayout(payout) {
      this.confirm({
        title: this.$pgettext('admin-payout', 'Delete payout'),
        message: this.$pgettext('admin-payout', 'Are you sure you want to delete this payout? This action cannot be undone.'),
        color: 'danger',
        cb_confirm: async () => {
          try {
            await this.deleteLastPayout(payout.id)
            await this.$refs.ajaxTable?.fetchList()
            await this.notifySuccess(this.$pgettext('admin-payout', 'Payout deleted successfully'))
          } catch (error) {
            await this.notifyError(this.$pgettext('admin-payout', 'Error deleting payout'))
          }
        }
      })
    },
    
    async downloadCSV(payout) {
      try {
        await this.downloadPayoutExcel(payout.id)
        await this.notifySuccess(this.$pgettext('admin-payout', 'Excel downloaded successfully'))
      } catch (error) {
        await this.notifyError(this.$pgettext('admin-payout', 'Error downloading Excel'))
      }
    },

    openRejectModal(payout) {
      this.$refs.rejectInvoiceModal.open(payout.id)
    },

    async onInvoiceRejected() {
      await this.$refs.ajaxTable?.fetchList()
    },

    async handleGeneratePayout() {
      const cmId = this.contractManagerId
      if (!cmId) return

      if (!confirm(this.$pgettext('admin-payout', 'Are you sure you want to generate a payout for this Contract Manager?'))) {
        return
      }

      try {
        this.loading = true
        await this.generatePayout(cmId)
        await Promise.all([
          this.$refs.ajaxTable?.fetchList(),
          this.fetchStatistics(cmId)
        ])
        await this.notifySuccess(this.$pgettext('admin-payout', 'Payout generated successfully'))
      } catch (error) {
        const status = error?.response?.status
        if (status === 409) {
          await this.notifyError(this.$pgettext('admin-payout', 'An unpaid payout already exists for this Contract Manager'))
        } else if (status === 404) {
          await this.notifyError(this.$pgettext('admin-payout', 'Contract Manager not found'))
        } else {
          await this.notifyError(this.$pgettext('admin-payout', 'Error generating payout'))
        }
      } finally {
        this.loading = false
      }
    }
  },
  async mounted() {
    await this.fetchConfig({type: 'admin_payouts'})
    if (this.contractManagerId) {
      try {
        await this.fetchStatistics(this.contractManagerId)
      } catch (e) {
        // Statistics are not critical, page works without them
      }
    }
  }
}
</script>